import React, { useMemo } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import { formatarDocumento, limparDocumento } from './documentoAlfanumerico';

const formatDocumentoCampo = digits => formatarDocumento(limparDocumento(digits).slice(0, 14));

const formatBRL = digits => {
  const v = (digits || '').replace(/\D/g, '').slice(0, 15);
  if (!v) return '';
  const n = parseInt(v, 10) / 100;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCep = digits => {
  const v = (digits || '').replace(/\D/g, '').slice(0, 8);
  const a = v.slice(0, 5);
  const b = v.slice(5, 8);
  return v.length <= 5 ? a : `${a}-${b}`;
};

const formatPhone = digits => {
  const v = (digits || '').replace(/\D/g, '').slice(0, 11);
  const a = v.slice(0, 2);
  const b = v.length > 10 ? v.slice(2, 7) : v.slice(2, 6);
  const c = v.length > 10 ? v.slice(7, 11) : v.slice(6, 10);
  if (!a) return '';
  if (!b) return `(${a})`;
  if (!c) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
};

const formatters = {
  cpfCnpj: formatDocumentoCampo,
  money: formatBRL,
  cep: formatCep,
  phone: formatPhone
};

const placeholders = {
  cpfCnpj: '000.000.000-00',
  money: '0,00',
  cep: '00000-000',
  phone: '(00) 00000-0000'
};

const digitLimit = {
  cpfCnpj: 14,
  money: 15,
  cep: 8,
  phone: 11,
  default: 20
};

const normalizeValue = (value, getFormat, maxDigits) => {
  if (getFormat === 'cpfCnpj') {
    return limparDocumento(value).slice(0, maxDigits);
  }

  return (value ?? '').toString().replace(/\D/g, '').slice(0, maxDigits);
};

const MaskedInput = React.forwardRef(
  ({ value, onChange, name, getFormat = 'cpfCnpj', placeholder, prefixo, ...textFieldProps }, ref) => {
    const maxDigits = digitLimit[getFormat] ?? digitLimit.default;
    const normalizedValue = useMemo(() => normalizeValue(value, getFormat, maxDigits), [
      value,
      getFormat,
      maxDigits
    ]);

    const fmt = useMemo(() => {
      if (typeof getFormat === 'function') return getFormat;
      if (getFormat && formatters[getFormat]) return formatters[getFormat];
      return d => d;
    }, [getFormat]);

    const masked = useMemo(() => fmt(normalizedValue), [fmt, normalizedValue]);

    const resolvedPlaceholder = useMemo(
      () => placeholder ?? placeholders[getFormat] ?? '',
      [placeholder, getFormat]
    );

    const mostrarPrefixo = prefixo && masked;
    const placeholderFinal = `${mostrarPrefixo ?? ''} ${resolvedPlaceholder}`.trim();
    const isCpfCnpj = getFormat === 'cpfCnpj';

    return (
      <TextField
        {...textFieldProps}
        inputRef={ref}
        name={name}
        value={masked}
        placeholder={placeholderFinal}
        onChange={e => {
          const nextValue = normalizeValue(e.target.value, getFormat, maxDigits);
          onChange?.({ target: { name, value: nextValue } });
        }}
        InputProps={{
          inputMode: isCpfCnpj ? 'text' : 'numeric',
          pattern: isCpfCnpj ? undefined : '[0-9]*',
          autoComplete: 'off',
          startAdornment: mostrarPrefixo ? (
            <InputAdornment position="start">{prefixo}</InputAdornment>
          ) : undefined
        }}
      />
    );
  }
);

MaskedInput.displayName = 'MaskedInput';
export default MaskedInput;
